/**
 * Configuration-pricing domain shapes (UID-346, ADR-0117 D6/D7/D8) — the
 * server-side mirror of `ConfigurationEstimateRequest` / `ConfigurationEstimate`
 * / `ConfigurationPricingOutcome` in `apps/api/openapi.yaml`.
 *
 * These live in `packages/shared`, not in `apps/api/src/acl/ports.ts`, for two
 * reasons. They are framework-agnostic domain shapes both apps handle (the modal
 * posts one, the BFF answers with the other); and, decisively, the §28
 * pricing-guardrail lives here, and D8 requires `pricing-guardrail.test.ts` to
 * carry the `RejectsAccountFacets` case for THIS input type — which it can only
 * do if the type is reachable from `packages/shared`. The PORT interface itself
 * (`ConfigurationPricingPort`) stays in `apps/api/src/acl/ports.ts` alongside its
 * siblings.
 *
 * @see manufacturing-catalog-lookup.ts for `RfqLineSpecification` + its validator
 * @see pricing-guardrail.ts for the §28 boundary this input type is bound by
 */
import { PROCESS_GROUPS } from './manufacturing-catalog'
import type { RfqLineSpecification } from './manufacturing-catalog-lookup'
import type { PartProcess } from './part-process'
import type { Price } from './price'

/**
 * Whether a configuration could be auto-estimated, or routes to a human.
 *
 * `manual_quote_required` is a NORMAL outcome, not an error — the design's own
 * modal footer renders it as a distinct state, and it is the expression of
 * DM§10's `PricedAndPromised → AwaitingQuote` edge and INT-CONFIG-06's "tell the
 * customer it is now custom".
 */
export const CONFIGURATION_PRICING_OUTCOMES = ['estimated', 'manual_quote_required'] as const

export type ConfigurationPricingOutcome = (typeof CONFIGURATION_PRICING_OUTCOMES)[number]

export function isConfigurationPricingOutcome(
  value: unknown,
): value is ConfigurationPricingOutcome {
  return (
    typeof value === 'string' &&
    (CONFIGURATION_PRICING_OUTCOMES as readonly string[]).includes(value)
  )
}

/**
 * The COMPLETE input to configuration price resolution — and deliberately
 * nothing more. The tuple is (specification, quantity, effectiveDate,
 * partSeed): the first three are the PRICE BASIS (ADR-0004, DM§28.1), and the
 * fourth is a DEMO-ONLY per-part variation seed, slated for deletion when the
 * real pricing service lands behind `ConfigurationPricingPort`.
 *
 * UID-604 / ADR-0177 widened the fixed triple to this fourth input, and with it
 * gave up a property worth naming: the estimate is NO LONGER a pure function of
 * the specification — two different parts with the same specification now price
 * differently. That is the point (a real quoting service prices on geometry,
 * size, complexity and setup, none of which this contract carries), and it is
 * the reason the widening got its own ADR rather than an amendment.
 *
 * §28 GUARDRAIL. This is the type `pricing-guardrail.ts` was written in
 * anticipation of: its doc comment states that "the future pricing-resolve
 * entrypoint MUST adopt `RejectsAccountFacets` on its input type and reject any
 * `accountFacetKeysIn` hit at runtime". `ConfigurationPricingPort.estimate` is
 * that entrypoint — the first one to exist in this repo — so it wraps this type
 * in `RejectsAccountFacets<…>` (compile time) and runs `accountFacetKeysIn`
 * (run time). Adding an AccountSegment / Vertical / Potential / FundingSignal
 * field here therefore fails to typecheck; smuggling one in at runtime throws.
 *
 * NO `accountId`, NO tenant, NO geometry, NO rfqId, NO line id. Two accounts
 * identical except for a §28 facet resolve to the SAME estimate, because the
 * account is not an input at all. `partSeed` does NOT weaken that boundary: it
 * is an opaque part identity, not an account facet, so `accountFacetKeysIn`
 * still reports nothing and `RejectsAccountFacets<ConfigurationEstimateInput>`
 * still resolves to the identity type. Two tenants each holding a part named
 * `Bracket-L` get the byte-identical estimate.
 */
export interface ConfigurationEstimateInput {
  readonly specification: RfqLineSpecification
  /**
   * The quantity RUNG to price at (DM§34.3's QuantityLadder/PricePoint). A
   * TRANSIENT pricing input — DM§35.2 refuses quantity as persisted
   * configuration, which is why it sits here and not on `RfqLineSpecification`.
   */
  readonly quantity: number
  /** ISO `YYYY-MM-DD` pricing effective date. */
  readonly effectiveDate: string
  /**
   * DEMO-ONLY per-part variation seed (UID-604, ADR-0177). An opaque stable
   * identity string for the part — a part name, else a part number. NOT a part
   * id, NOT looked up, NOT tenant-scoped, never persisted, never echoed.
   *
   * Omitted = "no per-part identity available", which prices at exactly the
   * un-seeded base (`seededDemoPrice('', …)` is par, 1.0x) — never $0, never
   * NaN. Same posture `auto-quote-policy.ts`'s `partSeed` already records.
   *
   * NOT a §28 facet: a part identity describes the thing being made, not who is
   * buying it, so two accounts still resolve the SAME estimate (DM§28.1).
   *
   * DELETE THIS FIELD when the real pricing service lands behind
   * `ConfigurationPricingPort` — a real dated rate card has no such input.
   */
  readonly partSeed?: string
}

/**
 * UID-439 / UID-605 — the app-edge caps on a part's DISPLAY identity, and
 * therefore on the pricing seed derived from it. They match the
 * `RfqLineView.part` contract caps (name ≤255, partNumber ≤128); the source
 * columns (`library.part.name` / `.catalog_sku` / `.customer_bom_id`) carry no
 * `maxLength` of their own.
 *
 * They live HERE, in `packages/shared`, and not on the API side where they were
 * first written, because ADR-0177 Amendment A4 makes {@link configurationPartSeed}
 * the single normalizer: the client and the server derive their identity strings
 * INDEPENDENTLY (the client projects the columns raw, the server through
 * `boundedOrNull`), so a cap that exists on only one side is a price
 * disagreement waiting to happen. `part-requirements-lookup.ts` re-exports these
 * as `MAX_PART_NAME_LENGTH` / `MAX_PART_SKU_LENGTH` — one definition, both sides.
 */
export const MAX_SEED_NAME_LENGTH = 255
export const MAX_SEED_SKU_LENGTH = 128

/** Trim, cap, and collapse every spelling of "absent" — `undefined`, `null`,
 * empty, whitespace-only — to `undefined`. Trims BEFORE capping so padding never
 * eats into the budget. Mirrors the server's `boundedOrNull` exactly, except that
 * it returns `undefined` rather than `null` (this function's contract), and it is
 * therefore IDEMPOTENT against already-bounded server input. */
function boundedSeed(value: string | null | undefined, cap: number): string | undefined {
  return value?.trim().slice(0, cap) || undefined
}

/**
 * UID-604 / ADR-0177 (amended by UID-605, Amendment A4) — the ONE `partSeed`
 * ladder AND the one normalizer for it: **name → catalog SKU → customer BOM id →
 * absent**, each trimmed and capped.
 *
 * Deliberately does NOT fall through to a sourceable/line id: that id is minted
 * per-request for a catalog result card (`auto-quote-policy.ts` calls it "the
 * weakest identity"), it is invisible to the client, and using it would make the
 * modal footer STRUCTURALLY unable to agree with the saved figure. Absence is the
 * honest answer and prices at the un-seeded base.
 *
 * WHY THE NORMALIZATION LIVES IN HERE (UID-605). It used to live on the server
 * only — `part-requirements-lookup.ts` trimmed, capped, and collapsed
 * `catalogSku ?? customerBomId`, while the client projected the very same
 * `library.part` columns RAW through `rowToSourceable` and had no BOM-id rung at
 * all. The two identity strings are **independently derived and never compared on
 * the wire**, so "the two adapters happen to agree" is not a property anything
 * enforced; the server's comment claiming its ladder "mirrors the FE" was simply
 * false. Centralizing it here makes agreement hold **by construction**. Two
 * independently-derived identity strings may only be compared after passing
 * through this function.
 *
 * The third rung matches the server's `boundedOrNull(catalogSku) ??
 * boundedOrNull(customerBomId)` collapse, and shares the SKU cap because it
 * occupies the same identity slot (a catalog part is identified by its SKU, a
 * custom/uploaded part by the customer's own BOM id).
 *
 * IDEMPOTENT against an already-bounded `LinePartIdentitySnapshot`, which is what
 * keeps `RfqService.buildSpecSelection` and `bulkConfigureLines`
 * behavior-preserving across this move.
 *
 * Four callers, one rule: `RfqService.buildSpecSelection` and `bulkConfigureLines`
 * (from the `linePartIdentity` snapshot), `auto-quote-runner.ts`'s seed map (same
 * snapshot), and the RFQ configurator footer — which reads the RAW identity the
 * client actually holds (`RfqBundleItem.sourceable` / `ReviewLineSourceable`), NOT
 * `RfqLineView.part` as §4.2 originally claimed; see Amendment A1.
 *
 * §6 still stands: this ladder and `auto-quote-policy`'s are NOT merged.
 */
export function configurationPartSeed(
  // Every field admits `undefined` as well as `null` deliberately, under this
  // repo's `exactOptionalPropertyTypes`: the server reads them off a
  // `LinePartIdentitySnapshot` (nullable columns) while the client reads them off
  // its own sourceable through optional chaining (`line.sourceable.name`, hence
  // `undefined`). One function has to take both spellings of "absent", or the
  // agreement rule above would need a cast at the very call site it protects.
  identity:
    | {
        readonly name?: string | null | undefined
        readonly sku?: string | null | undefined
        /** The customer's own BOM id — the third rung, for a BOM-sourced part
         * that has neither a library name nor a catalog SKU. */
        readonly bomId?: string | null | undefined
      }
    | undefined,
): string | undefined {
  return (
    boundedSeed(identity?.name, MAX_SEED_NAME_LENGTH) ??
    boundedSeed(identity?.sku, MAX_SEED_SKU_LENGTH) ??
    boundedSeed(identity?.bomId, MAX_SEED_SKU_LENGTH)
  )
}

/**
 * The ADVISORY estimate, or an explicit manual-quote outcome.
 *
 * `unitPrice` reuses the FROZEN shared `Price` value object (ADR-0004) with
 * `provenance: 'parametric'` — zero new money type. It MINTS NO ORDER-ELIGIBLE
 * PRICE: advisory display only, the ADR-0058 §4 posture, therefore out of SOX
 * scope. It can never satisfy `RFQ-OPTION-01` and must never flow into a
 * QuoteOption, Selection or OrderLine. Tooling/NRE EXCLUDED (RD-02 decision 2).
 *
 * `SpecSelection.unitPriceAmount` stays server-forced `null` pre-quote
 * (ADR-0085 D1, untouched) — this estimate lands on the separate
 * `SpecSelection.indicativeUnitPrice`, precisely so the binding and advisory
 * figures can never be confused.
 */
export interface ConfigurationEstimate {
  readonly outcome: ConfigurationPricingOutcome
  /** Non-null iff `outcome === 'estimated'`. Never order-eligible. */
  readonly unitPrice: Price | null
  /**
   * Why this configuration cannot be auto-estimated. Non-empty iff
   * `outcome === 'manual_quote_required'`, and surfaced verbatim so the buyer is
   * TOLD rather than silently routed (RFQ-ROUTE-01, INT-CONFIG-06).
   */
  readonly manualQuoteReasons: readonly string[]
  /**
   * Content version of the catalog + rate card the estimate was computed
   * against. Makes the basis auditable and lets a client detect a stale cached
   * figure. Advisory metadata — carries no money.
   */
  readonly catalogVersion: string
}

/**
 * The subset of a specification the manual-quote rule actually reads.
 *
 * Deliberately WIDER than {@link RfqLineSpecification} (every member optional
 * and nullable) so ONE function serves both callers without either having to
 * fabricate values: the server passes a fully-validated `RfqLineSpecification`,
 * which is structurally assignable; the modal passes its in-progress cascade
 * state, where the buyer may not have chosen a process yet. An unmade decision
 * is not a trigger — it is silence.
 */
export interface ManualQuoteSpecificationInput {
  readonly process?: PartProcess | null
  readonly certificates?: readonly string[] | null
  readonly customInspection?: boolean | null
  /**
   * DECLARED BUT NEVER READ. They are here so either caller can hand over a
   * whole specification — a validated `RfqLineSpecification` or a literal built
   * from the modal's cascade state — without TypeScript's excess-property check
   * forcing a projection at every call site. Widening the parameter is what makes
   * "one function, both callers" true in practice rather than only on paper.
   */
  readonly material?: string
  readonly finish?: string | null
  readonly inspections?: readonly string[] | null
}

/**
 * The SINGLE source of the specification-derived `manual_quote_required` rule
 * (ADR-0117 D4.2) — D8's three triggers, and nothing else.
 *
 * WHY THIS EXISTS. The rule was hand-kept in two places (this port's adapter and
 * `ConfigurePartsModal`) and the copies drifted: the client read "any inspection
 * selected" where the rule is "a CUSTOM inspection was requested", so choosing a
 * legitimate, auto-priceable catalog inspection told the buyer their parts route
 * to a human quote — a false statement about what they are buying and how fast.
 * A rule that decides commercial messaging must not exist twice. The D8 adapter
 * calls this and adds its rate-card-only reasons on top; the modal calls this and
 * ORs the server outcome on top. There is no second copy left to drift.
 *
 * PURE AND FRAMEWORK-FREE, and reasons rather than a boolean on purpose: the
 * modal can then render WHY, `manualQuoteReasons` gets one author, and
 * RFQ-ROUTE-01's "every line shows its state visibly, never silently" is
 * satisfiable from one place. Empty ⇒ no specification-derived trigger (which is
 * NOT the same as "auto-priceable" — the server may still have rate-card reasons).
 *
 * NOTE what is absent: `inspections` is not read at all. A listed inspection is
 * ordinary, auto-priceable vocabulary, and an UNLISTED one can never arrive —
 * membership is strictly enforced and an out-of-catalog name is a 400 (D4.1).
 * "Custom" is a routing state carried by `customInspection`, not a catalog value.
 */
export function specificationManualQuoteReasons(
  specification: ManualQuoteSpecificationInput,
): readonly string[] {
  const reasons: string[] = []

  // The real ERP grouping (processGroupingUtils' MANUAL_QUOTE_PROCESSES_ORDER
  // plus tube bending) — "quoted manually" is literally what that group means.
  const manualProcess = PROCESS_GROUPS.manualQuote.find(
    (entry) => entry.id === specification.process,
  )
  if (manualProcess !== undefined) {
    reasons.push(`${manualProcess.label} is quoted manually`)
  }

  if ((specification.certificates ?? []).length > 0) {
    reasons.push('Certificates requested')
  }

  if (specification.customInspection === true) {
    reasons.push('Custom inspection requested')
  }

  return reasons
}
